<template>
  <div class="passenger-travelcard">
    <template
      v-for="passenger in passengers"
      :key="passenger.id">
      <!-- Selected reisekort: green % badge + card name + remove -->
      <div
        v-if="passenger.discount_cards && passenger.discount_cards.length > 0"
        class="passenger-travelcard__selected">
        <span class="passenger-travelcard__badge">
          <svg class="passenger-travelcard__badge-icon">
            <use :xlink:href="`#${discount}`" />
          </svg>
        </span>
        <span class="passenger-travelcard__name">
          {{ cardLabel(passenger.discount_cards[0]) }}
        </span>
        <button
          class="passenger-travelcard__remove"
          @click="removeDiscountCard(passenger.id)"
          type="button"
          :aria-label="$t('components.button.cancel')">
          <svg class="passenger-travelcard__remove-icon">
            <use :xlink:href="`#${close}`" />
          </svg>
        </button>
      </div>

      <!-- Picking a reisekort -->
      <div
        v-else-if="dropdownVisible[passenger.id]"
        class="passenger-travelcard__picker">
        <Dropdown
          :options="cardTypes"
          :placeholder="$t('travelPlanner.passenger.travelCard.placeholder')"
          icon="discount"
          @update:selected="updateDiscountCard(passenger.id, $event)" />
        <button
          class="passenger-travelcard__remove"
          @click="removeDiscountCard(passenger.id)"
          type="button"
          :aria-label="$t('components.button.cancel')">
          <svg class="passenger-travelcard__remove-icon">
            <use :xlink:href="`#${close}`" />
          </svg>
        </button>
      </div>
    </template>

    <div
      v-if="hasAnyDiscountCard"
      class="passenger-travelcard__message">
      <span class="passenger-travelcard__circle">
        <svg class="passenger-travelcard__icon">
          <use :xlink:href="`#${warning}`" />
        </svg>
      </span>
      <span>{{ $t('travelPlanner.passenger.travelCard.reminder') }}</span>
    </div>

    <button
      v-if="canAddTravelcard"
      class="passenger-travelcard__add"
      @click="showDropdown"
      type="button">
      <span class="passenger-travelcard__add-icon">
        <svg>
          <use :xlink:href="`#${plus}`" />
        </svg>
      </span>
      {{ $t('travelPlanner.passenger.travelCard.addTravelCard') }}
    </button>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue'
  import close from '@/assets/icons/close.svg'
  import warning from '@/assets/icons/warning.svg'
  import discount from '@/assets/icons/discount.svg'
  import plus from '@/assets/icons/plus.svg'
  import { PassengerType, Passenger, DiscountCard } from '@/types/passengerTypes'
  import { fetchCardTypes } from '@/services/journeyService'
  import Dropdown from '@/components/BaseComponents/Dropdown.vue'

  interface Props {
    passengerType: PassengerType
    passengers: Passenger[]
  }

  interface Emits {
    (e: 'update-discount-card', passengerId: string, card: DiscountCard | null): void
  }

  const props = defineProps<Props>()
  const emit = defineEmits<Emits>()

  const cardTypes = ref<{ label: string; value: string }[]>([])
  const cardTypeData = ref<{ [key: string]: any }>({})
  const dropdownVisible = ref<{ [key: string]: boolean }>({})

  const hasCard = (passenger: Passenger) =>
    !!(passenger.discount_cards && passenger.discount_cards.length > 0)

  // Show the "add" link while at least one passenger of this type has neither a
  // selected card nor an open picker.
  const canAddTravelcard = computed(() => {
    return props.passengers.some(
      (passenger) => !hasCard(passenger) && !dropdownVisible.value[passenger.id],
    )
  })

  const hasAnyDiscountCard = computed(() => {
    return props.passengers.some((passenger) => hasCard(passenger))
  })

  const loadCardTypes = async () => {
    const response = await fetchCardTypes()

    const allowedCodes = [
      'SKYSS',
      'SKYSSKOLUMBUS',
      'KOLUMBUSVOKSEN',
      'KOLUMBUSUNGDOM',
      'KOLUMBUSSKOLE',
      'FARTE',
      'INNLANDSTRAFIKK',
    ]

    const filteredResponse = response.filter((card: any) =>
      allowedCodes.includes(card.code || card.value),
    )

    cardTypes.value = filteredResponse
    cardTypeData.value = filteredResponse.reduce((acc: any, card: any) => {
      acc[card.value] = card
      return acc
    }, {})
  }

  // Resolve a human-readable card label; passengers restored from the URL only
  // carry the code (name === code) until the card list has been fetched.
  const cardLabel = (card: DiscountCard) => cardTypeData.value[card.code]?.label || card.name

  const showDropdown = async () => {
    try {
      await loadCardTypes()

      for (const passenger of props.passengers) {
        if (!hasCard(passenger) && !dropdownVisible.value[passenger.id]) {
          dropdownVisible.value[passenger.id] = true
          break
        }
      }
    } catch (error) {
      console.error('Error fetching card types:', error)
    }
  }

  const updateDiscountCard = (passengerId: string, selectedValue: string) => {
    const cardData = cardTypeData.value[selectedValue]
    if (cardData) {
      const discountCard: DiscountCard = {
        code: cardData.value,
        description: cardData.label,
        name: cardData.label,
        requires_passenger_identifier: cardData.requires_passenger_identifier || false,
      }
      emit('update-discount-card', passengerId, discountCard)
    } else {
      emit('update-discount-card', passengerId, null)
    }
  }

  const removeDiscountCard = (passengerId: string) => {
    dropdownVisible.value[passengerId] = false
    emit('update-discount-card', passengerId, null)
  }

  onMounted(async () => {
    props.passengers.forEach((passenger) => {
      dropdownVisible.value[passenger.id] = hasCard(passenger)
    })
    // Resolve labels for already-selected cards (e.g. restored from the URL).
    if (props.passengers.some(hasCard)) {
      try {
        await loadCardTypes()
      } catch (error) {
        console.error('Error fetching card types:', error)
      }
    }
  })
</script>

<style lang="scss" scoped>
  .passenger-travelcard {
    display: flex;
    flex-direction: column;

    &__selected {
      display: flex;
      align-items: center;
      gap: rem(8);
      padding: rem(10) 0;
      border-bottom: 1px solid $clr-gray-100;
    }

    &__badge {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: rem(18);
      height: rem(18);
      background-color: $clr-success;
      border-radius: 50%;
    }

    &__badge-icon {
      width: rem(12);
      height: rem(12);
      color: $clr-white;
      fill: $clr-white;
    }

    &__name {
      @include body-md(400);
      flex: 1 0 0;
      min-width: 0;
      color: $clr-light-primary-900;
    }

    &__picker {
      display: flex;
      align-items: center;
      gap: rem(8);
      padding-top: rem(10);
    }

    &__remove {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: rem(24);
      height: rem(24);
      padding: 0;
      background: none;
      border: none;
      cursor: pointer;
    }

    &__remove-icon {
      width: rem(18);
      height: rem(18);
      color: $clr-primary-300;
    }

    &__add {
      display: flex;
      align-items: center;
      gap: rem(10);
      // 12px above the link (gap from the divider); the counter's 16px bottom
      // padding supplies the 16px below.
      padding: rem(12) 0 0;
      background: none;
      border: none;
      cursor: pointer;
      @include body-md(500);
      color: $clr-primary;
    }

    &__add-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: rem(22);
      height: rem(22);
      background-color: $clr-light-primary-50;
      border-radius: 50%;
      color: $clr-primary;

      // plus.svg's visible mark fills only 16 of its 24px viewBox, so an 18px box
      // renders the 12px plus mark the design specifies.
      svg {
        width: rem(18);
        height: rem(18);
        fill: currentColor;
      }
    }

    &__message {
      display: flex;
      align-items: flex-start;
      gap: rem(8);
      padding-top: rem(8);
      @include body-small;
    }

    &__circle {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: rem(2);
      flex-shrink: 0;
    }

    &__icon {
      width: rem(16);
      height: rem(16);
      color: $clr-primary-900;
    }
  }
</style>
